/* eslint-disable max-len */
/* eslint-disable no-underscore-dangle */
import { Map } from 'ol';
import { unByKey } from 'ol/Observable';
import { Group, Layer } from 'ol/layer';
import {
  useCallback,
  useContext, useEffect, useRef, useState,
} from 'react';
import ImageLayer from 'ol/layer/Image';
import dayjs, { Dayjs } from 'dayjs';
import { ImageStatic } from 'ol/source';
import ProductsContext from 'contexts/ProductsContext';
import { ImageSourceEvent } from 'ol/source/Image';
import { getQueueStats } from 'services/requestQueue';
import { debugLogger, LogLevel } from 'services/debugLogger';
import { App } from 'antd';
import { fetchAndCacheDTNTimestamps, getCachedTimestamps, setSampledTimestamps } from './dtn/dtnTimestampsCache';
import MainAppContext from '../../MainAppContext';
import TimeSeriesContext from '../../TimeSeriesContext';
import {
  cloneLayer, getLayerByName, recreateSource,
} from './utils';
import LayerName from './enums';
import { utcDayjs } from '../../../utils';
import { Product } from '../../../types/app.types';
import disposeLayerAfterRender from '../../../utils/layerLifecycle';
import { findClosestDTNTimestamp, prewarmDTNTiles } from './dtn/createDTNTileSource';

interface ImageSourceEventAndTime extends ImageSourceEvent {time?: string}

function useTimeSeriesPlayer(map: Map) {
  const { message } = App.useApp();
  const {
    animationState,
    setPreloadingState,
    setAnimationState,
    currentPosition,
    range,
    timestampList,
  } = useContext(TimeSeriesContext);
  const { editProductTime, activeProducts, selectedProducts } = useContext(ProductsContext);
  const { playbackMode, currentProjection } = useContext(MainAppContext);
  const [loadingAnimationCharge, setLoadingAnimationCharge] = useState<number | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const layerProgressMap = useRef<Record<string, number>>({});
  const activePreloadingLayers = useRef<Set<string>>(new Set());
  const completedPreloadingLayers = useRef<Set<string>>(new Set());

  // Function to update global progress based on all active layers
  const updateGlobalProgress = useCallback(() => {
    const active = activePreloadingLayers.current;
    const done = completedPreloadingLayers.current;
    const totalCount = active.size + done.size;

    if (totalCount === 0) {
      setLoadingAnimationCharge(null);
      return;
    }

    let sum = 0;
    active.forEach((id) => { sum += (layerProgressMap.current[id] || 0); });
    sum += done.size * 100;

    const avg = Math.round(sum / totalCount);
    setLoadingAnimationCharge(avg);

    if (active.size === 0 && done.size > 0) {
      setTimeout(() => {
        done.clear();
        setLoadingAnimationCharge(null);
      }, 500);
    }
  }, []);

  // Function to update progress for a specific layer
  const updateLayerProgress = useCallback((layerId: string, progress: number) => {
    layerProgressMap.current[layerId] = progress;
    updateGlobalProgress();
  }, [updateGlobalProgress]);

  // Function to add a layer to active preloading
  const addActiveLayer = useCallback((layerId: string) => {
    activePreloadingLayers.current.add(layerId);
    layerProgressMap.current[layerId] = 0;
    updateGlobalProgress();
  }, [updateGlobalProgress]);

  // Function to remove a layer from active preloading
  const removeActiveLayer = useCallback((layerId: string) => {
    activePreloadingLayers.current.delete(layerId);
    completedPreloadingLayers.current.add(layerId);
    updateGlobalProgress();
  }, [updateGlobalProgress]);

  const preloadGeoServerLayer = useCallback(({ index, product, generatedTimestampList }: { index: number; product: Product; generatedTimestampList: string[]; }) => new Promise<void>((resolve, reject) => {
    if (generatedTimestampList.length === 0) {
      resolve();
      return;
    }

    const geoLayer = getLayerByName(map, LayerName.GeoLayer) as Group;
    if (!geoLayer) {
      reject(new Error('GeoLayer not found'));
      return;
    }

    const layers = geoLayer.getLayers().getArray();
    const curLayer = layers.find((layer) => layer.get('id') === product.id && layer.get('layerIndex') === index) as ImageLayer<ImageStatic>;

    if (!curLayer) {
      reject(new Error(`Layer with id ${product.id} and index ${index} not found`));
      return;
    }

    const newLayer = cloneLayer(curLayer, currentProjection);
    geoLayer.getLayers().push(newLayer);

    const layerId = `${product.id}_${Date.now()}_${Math.random()}`;
    let prechargeFrameIndex = 0;
    let prechargeDownloadedIndex = 0;
    let interval: ReturnType<typeof setInterval> | null = null;
    let inactivityTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let hasResolved = false;

    // Add this layer to active preloading tracking
    addActiveLayer(layerId);

    const cleanup = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }

      if (inactivityTimeoutId) {
        clearTimeout(inactivityTimeoutId);
        inactivityTimeoutId = null;
      }

      // Remove the temporary layer; its resources are released after the next render so the
      // map's last frame never references a disposed layer.
      try {
        geoLayer.getLayers().remove(newLayer);
        disposeLayerAfterRender(map, newLayer);
      } catch (e) {
        debugLogger.timeSeries(LogLevel.WARN, 'Error removing/disposing newLayer', { error: e });
      }

      // Remove from active tracking
      removeActiveLayer(layerId);
    };

    // Function to reset inactivity timeout with intelligent queue checking
    const resetInactivityTimeout = () => {
      if (inactivityTimeoutId) {
        clearTimeout(inactivityTimeoutId);
      }

      inactivityTimeoutId = setTimeout(() => {
        if (prechargeDownloadedIndex < generatedTimestampList.length && !hasResolved) {
          // Get current queue stats to make intelligent decision
          const queueStats = getQueueStats();

          // Only terminate if queue is relatively empty (≤ active products count)
          // This means no significant backlog from other layers
          const shouldTerminate = queueStats.queueLength <= 4;

          debugLogger.timeSeries(LogLevel.DEBUG, 'Inactivity timeout check for layer', {
            layerIndex: newLayer.get('layerIndex'),
            queueLength: queueStats.queueLength,
            shouldTerminate,
            downloaded: `${prechargeDownloadedIndex}/${generatedTimestampList.length}`,
          });

          if (shouldTerminate) {
            hasResolved = true;
            cleanup();

            // Update the specific layer states even on timeout
            setAnimationState((prev) => {
              const newStates = [...prev];
              if (newStates[index]) {
                newStates[index] = 'active';
              }
              return newStates;
            });

            setPreloadingState((prev) => {
              const newStates = [...prev];
              if (newStates[index]) {
                newStates[index] = 'ended';
              }
              return newStates;
            });

            debugLogger.timeSeries(LogLevel.INFO, 'Preloading completed by intelligent inactivity timeout', {
              layerIndex: newLayer.get('layerIndex'),
              imagesLoaded: prechargeDownloadedIndex,
              totalImages: generatedTimestampList.length,
              queueLength: queueStats.queueLength,
              reason: 'Queue is clear',
            });
            resolve();
          } else {
            debugLogger.timeSeries(LogLevel.DEBUG, 'Preloading continues - queue still busy', {
              layerIndex: newLayer.get('layerIndex'),
              queueLength: queueStats.queueLength,
              action: 'Resetting timeout',
            });
            // Reset timeout to check again in 60 seconds
            resetInactivityTimeout();
          }
        }
      }, 60000); // 60 seconds of inactivity
    };

    const checkCompletion = () => {
      if (prechargeDownloadedIndex >= generatedTimestampList.length && !hasResolved) {
        hasResolved = true;
        cleanup();

        // Update the specific layer states similar to prechargeAnimation
        setAnimationState((prev) => {
          const newStates = [...prev];
          if (newStates[index]) {
            newStates[index] = 'active';
          }
          return newStates;
        });

        setPreloadingState((prev) => {
          const newStates = [...prev];
          if (newStates[index]) {
            newStates[index] = 'ended';
          }
          return newStates;
        });

        debugLogger.timeSeries(LogLevel.INFO, 'Preloading completed successfully', {
          layerIndex: newLayer.get('layerIndex'),
          totalImages: generatedTimestampList.length,
        });
        resolve();
      }
    };

    // Initialize the inactivity timeout
    resetInactivityTimeout();

    interval = setInterval(() => {
      if (prechargeFrameIndex >= generatedTimestampList.length) {
        return; // Wait for all downloads to complete
      }

      const currentTimestamp = generatedTimestampList[prechargeFrameIndex];
      const currentProgress = (prechargeDownloadedIndex * 100) / generatedTimestampList.length;

      // Capture the current frame index for use in event handlers
      const currentFrameIndex = prechargeFrameIndex;

      debugLogger.timeSeries(LogLevel.DEBUG, 'Preloading frame processing', {
        frameIndex: currentFrameIndex,
        totalFrames: generatedTimestampList.length,
        progress: `${currentProgress.toFixed(1)}%`,
        timestamp: currentTimestamp,
      });

      newLayer.getSource()?.set('time', currentTimestamp);
      const source = recreateSource({
        layer: newLayer,
        currentProjection,
        name: LayerName.PrealoadingGeoLayer + dayjs().valueOf(),
      });

      source.on('imageloadstart', () => {
        // Stop processing if already resolved
        if (hasResolved) {
          return;
        }

        debugLogger.timeSeries(LogLevel.DEBUG, 'Starting preload image request', {
          layerIndex: newLayer.get('layerIndex'),
          frame: `${currentFrameIndex + 1}/${generatedTimestampList.length}`,
          timestamp: currentTimestamp,
        });
        // Update progress for this specific layer
        const startProgress = (prechargeDownloadedIndex * 100) / generatedTimestampList.length;
        updateLayerProgress(layerId, startProgress);
      });

      source.on('imageloaderror', () => {
        // Stop processing if already resolved
        if (hasResolved) {
          return;
        }

        prechargeDownloadedIndex += 1;
        debugLogger.timeSeries(LogLevel.WARN, 'Image preload error', {
          layerIndex: newLayer.get('layerIndex'),
          progress: `${prechargeDownloadedIndex}/${generatedTimestampList.length}`,
          frame: currentFrameIndex + 1,
        });
        const errorProgress = (prechargeDownloadedIndex * 100) / generatedTimestampList.length;
        updateLayerProgress(layerId, errorProgress);

        // Reset inactivity timeout since we got a response (even if error)
        resetInactivityTimeout();

        checkCompletion();
      });

      source.on('imageloadend', (event: ImageSourceEventAndTime) => {
        // Stop processing if already resolved
        if (hasResolved) {
          return;
        }

        const layerName = event.target.get('name');
        if (layerName && layerName.includes(LayerName.PrealoadingGeoLayer) && event.time) {
          prechargeDownloadedIndex += 1;
          const endProgress = (prechargeDownloadedIndex * 100) / generatedTimestampList.length;
          updateLayerProgress(layerId, endProgress);
          debugLogger.timeSeries(LogLevel.DEBUG, 'Image preload completed', {
            layerIndex: newLayer.get('layerIndex'),
            progress: `${prechargeDownloadedIndex}/${generatedTimestampList.length}`,
            percentComplete: `${endProgress.toFixed(1)}%`,
          });

          // Reset inactivity timeout since we successfully downloaded an image
          resetInactivityTimeout();

          checkCompletion();
        }
      });

      newLayer.setSource(source);
      prechargeFrameIndex += 1;
    }, 100);
  }), [currentProjection, map, addActiveLayer, removeActiveLayer, updateLayerProgress, setAnimationState, setPreloadingState]);

  const cancelPrechargeAnimation = () => {
    if (timer.current) {
      clearInterval(timer.current);
    }
  };

  /**
   * Preloads DTN tile layers by directly fetching tiles for the current viewport
   * via the browser Fetch API, warming the HTTP cache without going through OL's
   * rendering pipeline (avoids opacity=0 / render-cycle tile-skip issues).
   */
  const preloadDTNLayerImpl = useCallback(({
    index, product, generatedTimestampList,
  }: { index: number; product: Product; generatedTimestampList: string[] }) => new Promise<void>((resolve) => {
    const markDone = () => {
      setAnimationState((prev) => {
        const s = [...prev];
        if (s[index]) s[index] = 'active';
        return s;
      });
      setPreloadingState((prev) => {
        const s = [...prev];
        if (s[index]) s[index] = 'ended';
        return s;
      });
      resolve();
    };

    const run = async () => {
      if (generatedTimestampList.length === 0) { markDone(); return; }

      const layerId = product.internalName;
      const dtnTimestamps = await fetchAndCacheDTNTimestamps(
        layerId,
        [range[0], range[1]] as [Dayjs, Dayjs],
      );
      if (dtnTimestamps.length === 0) { markDone(); return; }

      const uniqueTileSetIds = Array.from(new Set(
        generatedTimestampList
          .map((iso) => findClosestDTNTimestamp(dtnTimestamps, iso)?.tileSetId)
          .filter(Boolean) as string[],
      ));
      if (uniqueTileSetIds.length === 0) { markDone(); return; }

      // Store only the preloaded timestamps so replay can search within this
      // subset and guarantee a cache hit on every frame.
      const tileSetIdSet = new Set(uniqueTileSetIds);
      setSampledTimestamps(layerId, dtnTimestamps.filter((ts) => tileSetIdSet.has(ts.tileSetId)));

      const trackId = `dtn_${product.id}_${Date.now()}`;
      addActiveLayer(trackId);
      try {
        await prewarmDTNTiles(layerId, uniqueTileSetIds, map, (ratio) => {
          updateLayerProgress(trackId, ratio * 100);
        });
      } finally {
        updateLayerProgress(trackId, 100);
        removeActiveLayer(trackId);
        markDone();
      }
    };

    run().catch(() => markDone());
  }), [map, addActiveLayer, removeActiveLayer, updateLayerProgress, setAnimationState, setPreloadingState, range]);

  const preloadLayer = useCallback((params: { index: number; product: Product; generatedTimestampList: string[] }) => {
    if (params.product.provider === 'dtn') {
      return preloadDTNLayerImpl(params);
    }
    return preloadGeoServerLayer(params);
  }, [preloadDTNLayerImpl, preloadGeoServerLayer]);

  const preloadAllLayers = useCallback(async (timestampList: string[]) => {
    debugLogger.timeSeries(LogLevel.INFO, 'Preload all layers starting', {
      timestampListLength: timestampList.length,
      activeProducts,
      selectedProductsCount: selectedProducts.length,
    });

    try {
      const geoLayer = getLayerByName(map, LayerName.GeoLayer) as Group;
      if (!geoLayer) {
        debugLogger.timeSeries(LogLevel.ERROR, 'GeoLayer not found for preloadAllLayers');
        return;
      }

      debugLogger.timeSeries(LogLevel.DEBUG, 'GeoLayer found successfully');

      const generatedTimestampList = timestampList;

      // Get all layers from GeoLayer
      const allLayers = geoLayer.getLayers().getArray();
      debugLogger.timeSeries(LogLevel.DEBUG, 'Found layers in GeoLayer', {
        layerCount: allLayers.length,
      });

      // Create an array of promises for all active products
      const preloadPromises: Promise<void>[] = [];

      // Iterate through active products and preload each layer
      activeProducts.forEach((productIndex) => {
        debugLogger.timeSeries(LogLevel.DEBUG, 'Processing active product', {
          productIndex,
        });

        const product = selectedProducts[productIndex];
        if (!product) {
          debugLogger.timeSeries(LogLevel.WARN, 'Product not found in selectedProducts', {
            productIndex,
          });
          return;
        }

        debugLogger.timeSeries(LogLevel.DEBUG, 'Found product', {
          productName: product.name,
          productId: product.id,
          productIndex,
        });

        // Find the corresponding layer in GeoLayer
        const layer = allLayers.find((l) => l.get('id') === product.id && l.get('layerIndex') === productIndex);
        if (!layer) {
          debugLogger.timeSeries(LogLevel.WARN, 'Layer not found for product', {
            productId: product.id,
            productIndex,
            availableLayers: allLayers.map((l) => ({ id: l.get('id'), layerIndex: l.get('layerIndex') })),
          });
          return;
        }

        debugLogger.timeSeries(LogLevel.DEBUG, 'Found matching layer for product', {
          productName: product.name,
        });

        // Add preload promise for this layer
        const preloadPromise = preloadLayer({
          index: productIndex,
          product,
          generatedTimestampList,
        });

        preloadPromises.push(preloadPromise);
        debugLogger.timeSeries(LogLevel.DEBUG, 'Added preload promise for product', {
          productName: product.name,
        });
      });

      if (preloadPromises.length === 0) {
        debugLogger.timeSeries(LogLevel.WARN, 'No active products found to preload');
        return;
      }

      debugLogger.timeSeries(LogLevel.INFO, 'Starting preload for active layers', {
        layerCount: preloadPromises.length,
      });

      // Wait for all preload operations to complete
      const results = await Promise.allSettled(preloadPromises);

      // Log results
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          debugLogger.timeSeries(LogLevel.DEBUG, 'Preload completed successfully', {
            layerIndex: index + 1,
          });
        } else {
          debugLogger.timeSeries(LogLevel.ERROR, 'Preload failed', {
            layerIndex: index + 1,
            error: result.reason,
          });
        }
      });

      debugLogger.timeSeries(LogLevel.INFO, 'All layer preloading operations completed', {
        totalLayers: preloadPromises.length,
        successCount: results.filter((r) => r.status === 'fulfilled').length,
        failureCount: results.filter((r) => r.status === 'rejected').length,
      });
    } catch (error) {
      debugLogger.timeSeries(LogLevel.ERROR, 'Error in preloadAllLayers', {
        error,
      });
    }
  }, [map, activeProducts, selectedProducts, preloadLayer]);

  // Trigger when time series playback begins
  useEffect(() => {
    try {
      const geoLayer = getLayerByName(map, LayerName.GeoLayer) as Group;
      const allLayers = geoLayer?.getLayersArray() as Layer<ImageStatic>[];
      if (geoLayer && playbackMode && animationState.every((state) => state === 'active' || !state)) {
        allLayers.forEach((layer) => {
          if (layer.get('selfManagesTime')) return;
          if (currentPosition) {
            layer.getSource()?.set('time', currentPosition);
            const source = recreateSource({ layer, currentProjection });
            source.on('imageloadend', (event: ImageSourceEventAndTime) => {
              if (event.time) {
                editProductTime(layer.get('layerIndex'), event.time !== 'outOfRange' ? utcDayjs(event.time) : undefined);
              }
            });
            layer.setSource(source);
          }
        });
      }
    } catch (e) {
      debugLogger.timeSeries(LogLevel.ERROR, 'Error in useTimeSeriesPlayer during playback', {
        error: e,
      });
    }
  }, [animationState, currentPosition, currentProjection, map, playbackMode, editProductTime]);

  // Trigger (mostly) when time series slider changes and realtime
  useEffect(() => {
    try {
      if (!playbackMode) {
        const geoLayer = getLayerByName(map, LayerName.GeoLayer) as Group;
        const allLayers = geoLayer?.getLayersArray() as Layer<ImageStatic>[];
        if (geoLayer) {
          allLayers.forEach((layer) => {
            if (layer.get('selfManagesTime')) return;
            layer.getSource()?.set('time', currentPosition);
            const source = recreateSource({ layer, currentProjection });
            source.on('imageloadend', (event: ImageSourceEventAndTime) => {
              if (event.time) {
                editProductTime(layer.get('layerIndex'), event.time !== 'outOfRange' ? utcDayjs(event.time) : undefined);
              }
            });
            layer.setSource(source);
          });
        }
      }
    } catch (e) {
      message.error(`An error occurred while updating layer. ${e}`);
    }
  }, [currentPosition, editProductTime, map, playbackMode, currentProjection, message]);

  // Re-warm the browser tile cache for DTN layers after the user zooms or pans.
  // XYZ tile URLs encode {z}/{x}/{y}, so a viewport change requires fetching
  // new tiles for every tileSetId already in the replay range's cache.
  useEffect(() => {
    if (!timestampList.length) return;

    const debounceMs = 500;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const handleMoveEnd = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        activeProducts.forEach((productIndex) => {
          const product = selectedProducts[productIndex];
          if (!product || product.provider !== 'dtn') return;

          const layerId = product.internalName;
          const dtnTimestamps = getCachedTimestamps(layerId);
          if (!dtnTimestamps?.length) return;

          const uniqueTileSetIds = Array.from(new Set(dtnTimestamps.map((ts) => ts.tileSetId)));

          if (uniqueTileSetIds.length) {
            prewarmDTNTiles(layerId, uniqueTileSetIds, map).catch(() => {});
          }
        });
      }, debounceMs);
    };

    const key = map.on('moveend', handleMoveEnd);

    // eslint-disable-next-line consistent-return
    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      unByKey(key);
    };
  }, [map, timestampList, activeProducts, selectedProducts]);

  return {
    loadingAnimationCharge, cancelPrechargeAnimation, preloadLayer, preloadAllLayers,
  };
}

export default useTimeSeriesPlayer;
